import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApolloClient as apolloUseApolloClient, useQuery } from '@apollo/client';
import { graphqlErrorsToUiErrors } from '../../../api';
import ErrorMessage from '../../../components/error-message';
import { useDocumentTeamPermissions } from '../shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { getTeamsForUser } from '../../../utils/api/get-teams-for-user/get-teams-for-user';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { DocumentProvider, useDocumentState } from '../../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../../utils/document-wizard-utils/document-state-from-response';
import { TemplatePage } from './TemplatePage';
import {
  getDocumentTemplateById as getDocumentTemplateByIdQuery,
} from '../../../api/queries_v2';
import LoadingSpinner from '../../../components/loading-spinner';
import { useNotifications } from '../../../notifications';
import '../shared/document-wizard-page.scss';
import { TemplateType } from '../../../consts/forms-types';
import { useLeaseDocumentTemplateSave } from '../shared/hooks/use-document-save/use-document-save.hook';
import { updateDocumentTemplateDraft } from '../../../utils/api/update-document-offer/update-document';
import { useIntl } from 'react-intl';
import { useDeal } from '../../../contexts/DealContext';
import { getDocumentTemplateById } from '../../../utils/api/get-template-by-id';
import { useLeaseDocumentTemplateTitleSave } from '../shared/hooks/use-document-title-save/use-document-title-save.hook';

type TemplatePageContainerProps = {
  routeParams?: { templateId: string; teamId: string };
  useApolloClient?: (...args: any[]) => any;
  isOrganizationTemplate?: boolean;
};

const TemplatePageContainerInner = ({
  useApolloClient = apolloUseApolloClient,
  isOrganizationTemplate,
}: TemplatePageContainerProps) => {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const [, { error: notifyError }] = useNotifications();
  const reactIntl = useIntl();
  const { templateId, teamId } = useParams();
  const { setCurrentDeal } = useDeal();
  const { setOwnedTeams, setAllTeams } = usePermissions();
  const [templateGraphqlError, setTemplateGraphqlError] = useState(null);
  const dealTemplatesUrl = `/teams/${teamId}/templates`;
  const dealClausesUrl = `/teams/${teamId}/clauses`;
  const { state: documentState, updateDocumentState, resetDocumentState } = useDocumentState();
  const template = documentState.template;
  const isPending = documentState.isPending;

  useEffect(
    function cleanUpOnMount() {
      resetDocumentState();
    },
    [resetDocumentState]
  );

  useEffect(
    function fetchTeamsForUserOnMount() {
      getTeamsForUser()(apolloClient).then(({ ownedTeams, allTeams }) => {
        setOwnedTeams(ownedTeams);
        setAllTeams(allTeams);
      });
    },
    [apolloClient, setOwnedTeams, setAllTeams]
  );

  const permissions = useDocumentTeamPermissions({ apolloClient, teamId });
  const canCommentDocument = permissions.hasCommentPermission;

  const {
    loading: isDocumentTemplateByIdDataLoading,
    data: documentTemplateByIdData,
    refetch: refetchTemplate,
  } = useQuery(getDocumentTemplateByIdQuery, {
    fetchPolicy: 'network-only',
    skip: !templateId,
    variables: {
      documentTemplateId: templateId,
      teamId: teamId,
    },
  });

  const handleError = useCallback(() => {
    notifyError({
      message: reactIntl.formatMessage({
        id: 'lease-document-template-wizard.fetch-error',
        defaultMessage: 'Unable to fetch the Template',
      }),
      pinned: false,
    });
  }, [notifyError, reactIntl]);

  useEffect(() => {
    if (template !== null) {
      return;
    }

    if (!templateId) {
      return;
    }

    getDocumentTemplateById(templateId)(apolloClient)
      .then((response) => {
        setCurrentDeal(response.deal);
        updateDocumentState(documentStateFromResponse(response));
      })
      .catch((err) => {
        setTemplateGraphqlError(err);
        handleError();
      });
  }, [apolloClient, updateDocumentState, handleError, template, templateId]);

  const templateByIdData = documentTemplateByIdData?.getDocumentTemplateById;

  const handleSaveError = useCallback(() => {
    notifyError({
      message: reactIntl.formatMessage({
        id: 'shared-offer-page.save-error',
        defaultMessage: 'Unable to save the Document',
      }),
      pinned: false,
    });
  }, [notifyError, reactIntl]);

  const handleSave = useLeaseDocumentTemplateSave({
    id: templateId || '',
    save: updateDocumentTemplateDraft,
    onError: handleSaveError,
    apolloClient,
  });

  const saveTitle = useLeaseDocumentTemplateTitleSave({
    id: templateId || '',
    save: updateDocumentTemplateDraft,
    onError: handleSaveError,
    apolloClient,
  });

  const handleTitleSave = useCallback(
    (title) => {
      if (isPending) {
        return;
      }

      saveTitle(title);
    },
    [isPending, saveTitle]
  );

  const handleTemplateClose = useCallback(async () => {
    const currentTemplateData = templateByIdData;

    const closeDocumentLink =
      currentTemplateData?.templateType === TemplateType.CLAUSE ? dealClausesUrl : dealTemplatesUrl;

    navigate(closeDocumentLink);
  }, [
    templateByIdData,
    dealClausesUrl,
    dealTemplatesUrl,
    navigate,
  ]);

  const storedTitle = documentState.title;

  const refetchTemplateWrapper = useCallback(async () => {
    try {
      const result = await refetchTemplate();
      return result;
    } catch (error) {
      throw error;
    }
  }, [refetchTemplate]);

  // Handle GraphQL permission errors
  if (!template && templateGraphqlError) {
    const errors = graphqlErrorsToUiErrors(templateGraphqlError);
    return <ErrorMessage>{errors._general}</ErrorMessage>;
  }

  // Wait for both template data AND permissions to be ready
  const isTemplateLoading =
    (!documentTemplateByIdData && isDocumentTemplateByIdDataLoading) ||
    !template ||
    !templateId ||
    !teamId;

  if (isTemplateLoading) {
    return <LoadingSpinner />;
  }

  // Calculate edit permissions considering shared template status - AFTER data is loaded
  const templateData = templateByIdData;

  // Safety check: ensure we have template data before calculating shared status
  const isSharedFromWorkspace = templateData?.sharedFromTeam != null;

  // Calculate final edit permission - must have permission AND not be viewing shared template
  const canEditTemplate = Boolean(permissions.hasEditTemplatesPermission && !isSharedFromWorkspace);

  return (
    <TemplatePage
      canEdit={canEditTemplate}
      canComment={canCommentDocument}
      onSave={!isOrganizationTemplate ? handleSave : () => {}}
      documentId={templateId}
      teamId={teamId}
      permissions={permissions}
      onTitleSave={handleTitleSave}
      onTemplateClose={handleTemplateClose}
      documentTemplateData={templateByIdData}
      isOrganizationTemplate={isOrganizationTemplate}
      templateId={templateId}
      template={template}
      templateTitle={storedTitle || ''}
      lastUpdated={documentTemplateByIdData?.getDocumentTemplateById?.updatedAt}
      isTemplateLoaded={!isTemplateLoading}
      onRefetchTemplate={!isOrganizationTemplate ? refetchTemplateWrapper : undefined}
    />
  );
};

export const TemplatePageContainer = (props: TemplatePageContainerProps) => (
  <DocumentProvider>
    <TemplatePageContainerInner {...props} />
  </DocumentProvider>
);
